import styled from "styled-components";
import GridColumn from "./grid/GridColumn";
import GridRow from "./grid/GridRow";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import { Link } from "gatsby";
import EditorialBlockVideo from "./plp/EditorialBlockVideo";
import stripPTags from "../utils/stripPTag";

const BrandModuleWrapper = styled.div`
  .r-grid-row {
    @media (max-width: 749px) {
      gap: var(--spacing-1_5);
    }
  }
`;

const AssetWrapper = styled.div`
  width: 100%;
  max-height: 800px;
  height: 100%;
  aspect-ratio: 1/1;
  overflow: hidden;
  position: relative;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

const Title = styled.h2`
  margin-bottom: var(--spacing-1);

  @media (min-width: 933px) {
    margin-bottom: var(--spacing-1_5);
  }
`;

const Description = styled.p`
  margin-bottom: var(--spacing-1_5);

  @media (min-width: 933px) {
    margin-bottom: var(--spacing-2);
  }
`;

const LinksList = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--spacing-1);
  padding: 0;
  list-style: none;

  @media (min-width: 933px) {
    gap: var(--spacing-2);
    margin-bottom: var(--spacing-2);
  }
`;

const LinkItem = styled.li`
  a {
    border-bottom: 2px solid var(--indigo-blue);
    padding-bottom: 2px;
    transition: all 0.3s ease-in-out;

    &:hover {
      color: var(--indigo-blue-60);
      border-color: var(--indigo-blue-60);
    }
  }
`;

interface ChildMarkdownRemark {
  childMarkdownRemark: {
    html: string;
  };
}

interface BrandModuleProps {
  image?: {
    gatsbyImageData: IGatsbyImageData;
    description: string;
  };
  video?: {
    captions?: any;
    description?: {
      description: string;
    };
    stream?: {
      videoUpload: {
        playbackId: string;
      };
    };
    title: string;
    videoLength?: number;
    hasAudio?: boolean;
  };
  headline?: ChildMarkdownRemark;
  subhead?: ChildMarkdownRemark;
  links?: any[];
  placement?: "Left" | "Right" | "Fullwidth";
}

const BrandModule = ({
  image,
  video,
  headline,
  subhead,
  links,
  placement = "Left",
}: BrandModuleProps) => {
  const isLeft = placement === "Left";
  return (
    <BrandModuleWrapper>
      <GridRow>
        <GridColumn
          xs={{ size: 6, order: 0 }}
          s={{ size: 6, order: isLeft ? 0 : 3 }}
          m={{ size: 6, order: isLeft ? 0 : 3 }}
        >
          <AssetWrapper>
            {image && !video && (
              <GatsbyImage
                image={image?.gatsbyImageData}
                alt={image?.description}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )}
            {video && (
              <EditorialBlockVideo
                item={{
                  content: {
                    videoContent: video,
                  },
                  size: "small",
                }}
              />
            )}
          </AssetWrapper>
        </GridColumn>
        <GridColumn
          xs={{ size: 6, hide: true }}
          s={{ size: 1, order: isLeft ? 1 : 0 }}
          m={{ size: 1, order: isLeft ? 1 : 0 }}
        />
        <GridColumn
          xs={{ size: 6, order: 1 }}
          s={{ size: 4, order: isLeft ? 2 : 1 }}
          m={{ size: 4, order: isLeft ? 2 : 1 }}
        >
          <ContentWrapper>
            <Title
              className="typography-headline2"
              dangerouslySetInnerHTML={{
                __html: stripPTags(headline?.childMarkdownRemark?.html || ""),
              }}
            />
            <Description
              className="typography-body1 font-circular"
              dangerouslySetInnerHTML={{
                __html: stripPTags(subhead?.childMarkdownRemark?.html || ""),
              }}
            />
            {links && (
              <LinksList>
                {links.map((link) => (
                  <LinkItem key={link.text}>
                    <Link
                      to={link.to.path}
                      title={link.text}
                      className="typography-label2"
                      aria-label={link.ariaLabel || link.text}
                    >
                      {link.text}
                    </Link>
                  </LinkItem>
                ))}
              </LinksList>
            )}
          </ContentWrapper>
        </GridColumn>
        {!isLeft && (
          <GridColumn
            xs={{ size: 6, hide: true }}
            s={{ size: 1, order: isLeft ? 1 : 2 }}
            m={{ size: 1, order: isLeft ? 1 : 2 }}
          />
        )}
      </GridRow>
    </BrandModuleWrapper>
  );
};

export default BrandModule;
